import type { ApolloCache } from '@apollo/client';

/**
 * Trim posts from the cached `getUserFeed` list. Since TRIL-177 the feed
 * renders from the `getUserFeed` cached query result, and since TRIL-179
 * `cache.modify` is the sole removal path (the redundant Redux dispatch was
 * dropped). Without this, the cached posts array still contains a removed post,
 * so the next remount/refocus re-renders the ghost.
 *
 * Removes matching posts from the `getUserFeed` list in every cached keyArgs
 * bucket. Only the feed *list* is trimmed — the normalized `Post:{id}` entities
 * are left intact (they get reused / refreshed elsewhere). Extracted from
 * recommendationFeed for testability + reuse (TRIL-52).
 */
export function evictPostsFromFeedCache(
  cache: ApolloCache<unknown>,
  shouldRemove: (post: Record<string, any>) => boolean,
): void {
  cache.modify({
    fields: {
      getUserFeed(existing: any, { readField }: any) {
        if (!existing?.posts) return existing;
        const posts = existing.posts.filter((ref: any) => {
          const candidate = {
            postId: readField('postId', ref),
            id: readField('id', ref),
            rootPostId: readField('rootPostId', ref),
            userId: readField('userId', ref),
            authorId: readField('authorId', ref),
            author: readField('author', ref),
          };
          return !shouldRemove(candidate);
        });
        return { ...existing, posts };
      },
    },
  });
}
